// fmo-retention-software.js - Tier 3 Solution Page
export const content = {
  heroSubtitle: "Give your downline the retention tools they need to protect their books—and your override revenue. Agency-wide retention management for FMOs who understand that keeping clients matters as much as writing them.",

  problemSection: {
    title: "The FMO Retention Problem",
    html: `
      <p>Here's a frustrating reality for FMOs: you spend resources recruiting agents, training them, and helping them build their books. Then they lose 15-25% of those clients annually because retention isn't systematized.</p>

      <p>Every client lost is revenue lost—not just for the agent, but for you. Your override structure is built on their book. When their retention suffers, your revenue suffers.</p>

      <p>The challenge is scale. You can't personally manage retention for every agent's book. But you can give them tools that make retention systematic instead of random. That's what Pro IRP provides: agency-wide retention infrastructure.</p>
    `
  },

  features: [
    {
      icon: "users",
      title: "Multi-Agent Management",
      description: "Agency dashboard with visibility into every agent's retention metrics. See who's retaining well, who needs help, and where the agency-wide issues are."
    },
    {
      icon: "chart",
      title: "Rollup Analytics",
      description: "Agency-wide retention rates, segment analysis, plan performance. Understand retention patterns across your entire downline, not just individual anecdotes."
    },
    {
      icon: "shield",
      title: "Standardized Workflows",
      description: "Create agency-wide workflow templates that every agent uses. Consistent retention processes across your downline without managing each agent individually."
    },
    {
      icon: "zap",
      title: "Training Integration",
      description: "Built-in best practices and guided workflows help new agents adopt retention habits from day one. The tool teaches while they work."
    },
    {
      icon: "bell",
      title: "Alert Escalation",
      description: "High-risk clients can trigger alerts not just to the agent, but to agency management. Catch critical retention risks before they become losses."
    },
    {
      icon: "target",
      title: "Performance Tracking",
      description: "Monitor agent retention performance over time. Identify who needs coaching, who's improving, and who's falling behind."
    }
  ],

  featuresTitle: "Agency-Wide Retention",

  mainContent: `
    <h2>Why FMOs Need Retention Tools</h2>

    <p>Most FMOs focus on production. Recruiting agents, driving enrollments, growing the book. That's important—but it's only half the equation.</p>

    <p>Consider the math:</p>
    <ul>
      <li>Agent with 100 clients at 80% retention: loses 20 clients/year</li>
      <li>Same agent at 90% retention: loses 10 clients/year</li>
      <li>Over 5 years: difference of 50 clients (cumulative)</li>
      <li>At $300/client average: $15,000 in retained commission</li>
      <li>Your override on that: significant over 5 years</li>
    </ul>

    <p>Now multiply that by every agent in your downline. The aggregate retention improvement across an FMO represents substantial revenue protection.</p>

    <h2>The Visibility Problem</h2>

    <p>Without proper tools, FMO leadership is flying blind on retention:</p>

    <ul>
      <li>What's our agency-wide retention rate?</li>
      <li>Which agents are retaining well vs. poorly?</li>
      <li>Which plans have systemic churn problems?</li>
      <li>Where are we losing clients—AEP? OEP? Year-round?</li>
      <li>What's causing the churn?</li>
    </ul>

    <p>Pro IRP provides the analytics to answer these questions at the agency level. You see patterns across your entire downline, not just individual agent anecdotes.</p>

    <h2>Agency Dashboard Features</h2>

    <h3>Executive Overview</h3>
    <ul>
      <li>Agency-wide retention rate (current and trend)</li>
      <li>Retention by segment (MA, Med Supp, by plan)</li>
      <li>Agent performance ranking</li>
      <li>High-risk clients across agency</li>
      <li>Upcoming critical dates (AEP prep, etc.)</li>
    </ul>

    <h3>Agent Performance View</h3>
    <ul>
      <li>Individual agent retention metrics</li>
      <li>Comparison to agency average</li>
      <li>Improvement trends over time</li>
      <li>Workflow completion rates</li>
      <li>Client touchpoint frequency</li>
    </ul>

    <h3>Drill-Down Analytics</h3>
    <ul>
      <li>Churn analysis by reason</li>
      <li>Plan-specific retention patterns</li>
      <li>First-year vs. established client retention</li>
      <li>Seasonal patterns (AEP, OEP impact)</li>
    </ul>

    <h2>Standardizing Best Practices</h2>

    <p>One of the biggest retention challenges at scale is consistency. Your best agents have great retention habits. Your newer agents don't know what they don't know.</p>

    <p>Pro IRP lets you:</p>

    <h3>Create Agency Workflow Templates</h3>
    <ul>
      <li>Define standard onboarding sequences</li>
      <li>Set required touchpoint cadences</li>
      <li>Establish AEP preparation protocols</li>
      <li>Build OEP defense workflows</li>
    </ul>

    <h3>Enforce Across Downline</h3>
    <ul>
      <li>Assign templates to all agents</li>
      <li>Track compliance with agency standards</li>
      <li>Identify agents who aren't following protocols</li>
      <li>Measure impact of standardization</li>
    </ul>

    <h3>Continuously Improve</h3>
    <ul>
      <li>Test different workflow variations</li>
      <li>Measure retention impact</li>
      <li>Update templates based on results</li>
      <li>Share learnings across agency</li>
    </ul>

    <h2>Training New Agents</h2>

    <p>New agents often don't learn retention until they've already lost clients. Pro IRP changes that:</p>

    <ul>
      <li><strong>Guided workflows</strong> show them what to do and when</li>
      <li><strong>Built-in prompts</strong> explain why each step matters</li>
      <li><strong>Templates</strong> give them proven language and approaches</li>
      <li><strong>Progress tracking</strong> reinforces good habits</li>
    </ul>

    <p>The tool teaches retention best practices while they work. By the time they've been using it for a year, retention habits are ingrained.</p>

    <h2>Alert Escalation</h2>

    <p>Some retention risks are too important to leave to the individual agent:</p>

    <ul>
      <li>Very high-value clients at risk</li>
      <li>Agents who haven't addressed critical alerts</li>
      <li>Pattern problems affecting multiple clients</li>
      <li>Compliance-sensitive situations</li>
    </ul>

    <p>Pro IRP can escalate these to agency management. You see what needs attention without micromanaging every agent.</p>

    <h2>Implementation at Scale</h2>

    <p>Deploying to an agency is straightforward:</p>

    <ol>
      <li><strong>Agency setup:</strong> Configure agency-level settings and templates</li>
      <li><strong>Agent onboarding:</strong> Invite agents to the platform</li>
      <li><strong>Data import:</strong> Bulk import client data (CSV or API)</li>
      <li><strong>Workflow assignment:</strong> Deploy standard workflows to all agents</li>
      <li><strong>Training:</strong> Brief orientation (system is intuitive)</li>
    </ol>

    <p>Most agencies are operational within a week. The ongoing value comes from consistent usage across the downline.</p>

    <h2>Pricing for FMOs</h2>

    <p>Pro IRP offers volume pricing for agencies:</p>

    <ul>
      <li>Per-agent pricing with volume discounts</li>
      <li>Agency management features at no additional cost</li>
      <li>Unlimited clients per agent</li>
      <li>Direct billing or markup flexibility</li>
    </ul>

    <p>Many FMOs pass the cost through to agents or subsidize it as a retention benefit. Either way, the ROI math works when retention improves.</p>
  `,

  faqs: [
    {
      question: "How does Pro IRP help FMOs specifically?",
      answer: "Agency-level visibility into retention across all agents, standardized workflow templates, performance tracking, alert escalation for high-risk situations, and rollup analytics. You see the agency-wide picture, not just individual agent anecdotes."
    },
    {
      question: "Can I create standard workflows for all my agents?",
      answer: "Yes. Create agency workflow templates (onboarding, quarterly touchpoints, AEP prep, etc.) and deploy them across your downline. Track compliance and measure which workflows drive the best retention results."
    },
    {
      question: "How does alert escalation work?",
      answer: "Configure which situations trigger escalation to agency management: high-value clients at risk, agents not addressing critical alerts, compliance-sensitive situations. You see what needs attention without micromanaging."
    },
    {
      question: "What does implementation look like for an agency?",
      answer: "Agency setup, agent onboarding (invitations), bulk data import, workflow deployment, brief training. Most agencies are operational within a week. Ongoing value comes from consistent usage across the downline."
    },
    {
      question: "What pricing options are available for FMOs?",
      answer: "Volume pricing with per-agent rates that decrease at scale. Agency management features included. Unlimited clients per agent. Flexible billing options (direct to FMO or through to agents)."
    },
    {
      question: "How do I measure ROI for my agency?",
      answer: "Track agency-wide retention before and after implementation. Even 5% improvement across a 20-agent agency with 100 clients each at $300/year = $30,000+ in protected commission annually. Plus your override revenue on all of it."
    }
  ],

  sources: [
    {
      title: "Medicare Advantage Distribution Trends",
      url: "https://www.kff.org/medicare/issue-brief/medicare-advantage-in-2024-enrollment-update-and-key-trends/",
      publisher: "Kaiser Family Foundation",
      accessedDate: "2024-12-10"
    }
  ]
};

export default content;
